// src/components/RecommendationPreviewDrawer.jsx
import React, { useState, useEffect, useMemo, useRef } from 'react'
import PropTypes from 'prop-types'
import axios from 'axios'
import {
  SwipeableDrawer,
  Box,
  Typography,
  Paper,
  Button,
  IconButton,
  Chip,
  Stack,
  Skeleton,
  Menu,
  MenuItem,
  Tooltip,
} from '@mui/material'
import { useTheme } from '@mui/material/styles'
import { useMediaQuery } from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import MoreVertIcon from '@mui/icons-material/MoreVert'
import { motion, AnimatePresence } from 'framer-motion'
import DiscontinuedDialog from './common/DiscontinuedDialog'
import { zeileFaelltAusDenEmpfehlungen, formatAuslaufTag } from '../utils/discontinued'
import { moqLage, MOQ_UNTERSCHRITTEN, MOQ_UNBEKANNT, MOQ_NICHT_LADBAR, MOQ_MENGE_UNKLAR, MOQ_LAEDT } from '../utils/moqWarnung'
import { v2Tone } from '../theme/theme'
import { anschriftEinzeiler } from './vendors/versandort';
import { formatMoney } from '../utils/format';

const API = process.env.REACT_APP_API_BASE_URL
const APP_BAR_H = 64  // must match SidebarAndHeaderBar / SlidePanel

function formatCurrency(x) {
  return formatMoney(Number(x) || 0)
}

const itemVariants = {
  hidden: { opacity: 0, y: 10 },
  visible: (i) => ({
    opacity: 1,
    y: 0,
    transition: { delay: i * 0.045, duration: 0.2, ease: 'easeOut' },
  }),
  exit: { opacity: 0, scale: 0.96, transition: { duration: 0.15 } },
}

export default function RecommendationPreviewDrawer({
  open,
  onOpen,
  onClose,
  group,
  products,
  produktLage = 'laedt',   // konservativ: wer sie vergisst, bekommt "laedt", nicht "unbekannt"
  onAccept,
  onDecline,
  onDiscontinued,
}) {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  const [vendor, setVendor] = useState(null)
  const [items, setItems] = useState([])
  const [zeilenMenu, setZeilenMenu] = useState({ anchorEl: null, item: null })
  const [auslaufFuer, setAuslaufFuer] = useState(null)
  // Der ⋮-Knopf, aus dem heraus der Dialog geoeffnet wurde — Ziel fuer die
  // Fokusrueckgabe. Siehe `schliesseAuslauf`.
  const ausloeserRef = useRef(null)

  // Abhaengig von `group?.vendor_id` und `open`, NICHT von der
  // Objektidentitaet von `group`.
  //
  // 🔴 Bis zum Regel-15-Gate stand hier die Begruendung, die Elternseite ziehe
  // die Gruppe nach einer Zeilenaktion nach „und genau das tut sie seit der
  // Auslauf-Aktion". **Das war gemessen falsch.** `setActiveGroup` wird an
  // GENAU EINER Stelle gerufen (`POOverview.jsx` › `handlePreview`);
  // `handleDiscontinued` fasst nur `setRecommendedOrders` an. Die Identitaet
  // von `group` bewegt sich waehrend einer offenen Sitzung also nie, und der
  // alte `[group]`-Effekt konnte die beschriebene Regression gar nicht
  // ausloesen. *Positivkontrolle:* derselbe Grep findet `setRecommendedOrders`
  // an fuenf Aufrufstellen (sechs Zeilen inkl. der Deklaration) — die Suche
  // schlaegt an, die Eins ist eine Aussage.
  //
  // Die Fassung hier ist damit **Vorsorge, kein Fix**: sie haelt den Effekt
  // auch dann still, wenn jemand `activeGroup` spaeter doch nachzieht.
  //
  // 🔴 `if (!open) return` ist NICHT kosmetisch. `open` wechselt in beide
  // Richtungen; ohne die Wache setzt der Effekt beim SCHLIESSEN `items` auf
  // die Liste vom Oeffnungszeitpunkt zurueck (`POOverview` leert `activeGroup`
  // nicht). Die Zeilen stehen in `AnimatePresence`, und der `SwipeableDrawer`
  // haelt seine Kinder bis zum Ende der Transition — lokal entfernte Zeilen
  // liefen also waehrend des Hinausgleitens sichtbar wieder ein.
  useEffect(() => {
    if (!open) return
    setItems(group?.recommended_orders ?? [])
  }, [group?.vendor_id, open]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!group) return
    setVendor(null)
    axios
      .get(`${API}/api/vendors/${group.vendor_id}`)
      .then(({ data }) => setVendor(data))
      .catch(console.error)
  }, [group?.vendor_id]) // eslint-disable-line react-hooks/exhaustive-deps

  const handleDeclineItem = async (sku) => {
    try {
      await onDecline(group.vendor_id, [{ sku }])
    } finally {
      setItems((prev) => {
        const next = prev.filter((i) => i.sku !== sku)
        if (next.length === 0) onClose()
        return next
      })
    }
  }

  const openZeilenMenu = (e, item) => {
    e.stopPropagation()
    ausloeserRef.current = e.currentTarget
    setZeilenMenu({ anchorEl: e.currentTarget, item })
  }

  // 🔴 Fokus von Hand zurueckgeben. `disableRestoreFocus` am Menue (gegen das
  // Flackern) nimmt dem Dialog seinen Wiederherstellungspunkt: er merkt sich
  // den Menueeintrag, und der ist beim Schliessen laengst abgehaengt.
  //
  // GEMESSEN, nicht hergeleitet: ohne diese Zeilen landet der Fokus auf dem
  // `MuiDrawer-paper` — NICHT auf `<body>`, wie die Quelltextverfolgung des
  // Regel-16-Gates vermutete. Der Bediener bleibt also im Drawer; es ist die
  // mildere Fassung des Befundes, aber der ⋮-Knopf ist trotzdem der richtige
  // Ort. Zugesichert in `tests/po/recommended-discontinue.spec.ts` (D.3).
  //
  // `isConnected` ist die Bedingung, nicht bloss Vorsicht: hat der Auslauf
  // sofort gewirkt, ist die Zeile samt ihrem ⋮ weg. Dann bleibt der Fokus, wo
  // der Dialog ihn laesst — auf dem Drawer —, und das ist richtig so.
  // 🔴 In einem `requestAnimationFrame`, nicht direkt. GEMESSEN: ein direkter
  // Aufruf hier wirkt NICHT — die Fokusfalle des Dialogs raeumt NACH `onClose`
  // auf und setzt den Fokus auf ihren eigenen Wiederherstellungspunkt, also
  // hinter meinen Aufruf. Der Rahmen danach gewinnt.
  const schliesseAuslauf = () => {
    setAuslaufFuer(null)
    const el = ausloeserRef.current
    if (!el || !el.isConnected) return
    requestAnimationFrame(() => {
      if (el.isConnected) el.focus()
    })
  }
  const closeZeilenMenu = () => setZeilenMenu({ anchorEl: null, item: null })

  // Nach dem Speichern des Auslaufs. `antwort` ist die Antwort des Endpunkts,
  // NICHT der lokale Eingabezustand — der Server ist die Quelle, und er
  // normalisiert (leere Notiz wird `null`).
  const handleDiscontinued = (sku, antwort) => {
    onDiscontinued?.(group.vendor_id, sku, antwort)

    if (!zeileFaelltAusDenEmpfehlungen(antwort?.discontinued_at)) {
      // Ankuendigung mit Vorlauf: die Zeile bleibt bestellbar und BLEIBT
      // stehen — genau wie `GET /api/recommended_purchase_orders` sie beim
      // naechsten Abruf wieder liefern wuerde.
      setItems((prev) => prev.map((i) => (
        i.sku === sku
          ? {
              ...i,
              discontinued_at: antwort.discontinued_at,
              discontinued_note: antwort.discontinued_note,
            }
          : i
      )))
      return
    }

    // Wirkt sofort: raus aus der Liste.
    //
    // ⚠️ `onClose` steht ABSICHTLICH nicht im `setItems`-Aktualisierer. Ein
    // Aktualisierer wird unter StrictMode doppelt ausgefuehrt, und ein
    // Seiteneffekt darin feuert dann zweimal. `handleDeclineItem` daneben
    // macht genau das — hier nicht nachgebaut.
    const rest = items.filter((i) => i.sku !== sku)
    setItems(rest)
    if (rest.length === 0) onClose()
  }

  const handleDeclineAll = async () => {
    try {
      await onDecline(group.vendor_id, items.map((i) => ({ sku: i.sku })))
    } finally {
      onClose()
    }
  }

  // O21 — EINE Aufloesung je Zeile, nicht zwei.
  // 🔴 Kein `|| {}` mehr: das machte "nicht gefunden" von "gefunden, leer"
  // ununterscheidbar — genau die Unterscheidung, auf der die MOQ-Auskunft steht.
  // 🔴 Schluessel als Zeichenketten, wie `price_references.py:41-52` es fuer
  // das Backend festhaelt.
  const produktJeZeile = useMemo(() => {
    const nachSku = new Map()
    for (const prod of products || []) {
      if (prod?.sku) nachSku.set(String(prod.sku).trim(), prod)
    }
    return (items || []).map((item) => {
      const sku = item?.sku == null ? null : String(item.sku).trim() || null
      return sku ? nachSku.get(sku) : undefined
    })
  }, [items, products])

  const moqLagen = useMemo(() => (items || []).map((item, i) => moqLage({
    zeilenMoq: null,                 // im Vorschlag gibt es noch keine Zeile
    stammMoq: produktJeZeile[i]?.moq,
    menge: item?.recommended_order_qty,
    produktLage,
    produktGefunden: produktJeZeile[i] !== undefined,
  })), [items, produktJeZeile, produktLage])

  const itemSubtotal = items.reduce((sum, item, i) => {
    const p = produktJeZeile[i]
    return sum + (Number(p?.cost_per_item) || 0) * (item.recommended_order_qty || 0)
  }, 0)
  // 🔴 EK-B6: die geschaetzte Fracht ist hier ENTFALLEN. Sie wurde aus dem
  // Lieferanten-Frachtsatz gerechnet — demselben Feld, das stillgelegt wird.
  // Die Vorschau zeigt jetzt nur noch, was sie belegen kann.
  //
  // 🟢 NACHTRAG 2026-09-16, Owner-Entscheid O43 (a): hier stand, dass die
  // ANGELEGTE Bestellung weiterhin schaetzt und deshalb von der Vorschau
  // abweichen kann. Das gilt nicht mehr — `calculate_summaries` schaetzt nicht
  // mehr, und die Bestellmail laesst die Frachtzeile bei 0 weg. Vorschau und
  // angelegte Bestellung sagen jetzt dasselbe: keine Fracht.
  const grandTotal = itemSubtotal

  const vendorLabel = vendor
    ? vendor.metaobject_name || vendor.vendor_name
    : group?.vendor_name || '…'

  return (
    <SwipeableDrawer
      anchor="right"
      open={open}
      onOpen={onOpen}
      onClose={onClose}
      // On desktop: offset below the app bar so the header never overlaps
      ModalProps={{ sx: { top: { sm: `${APP_BAR_H}px` } } }}
      PaperProps={{
        sx: {
          bgcolor: 'background.default',
          width: { xs: '100%', sm: 560, md: 680 },
          maxWidth: '100%',
          display: 'flex',
          flexDirection: 'column',
          // Sit below app bar on desktop
          top: { sm: `${APP_BAR_H}px` },
          height: { sm: `calc(100% - ${APP_BAR_H}px)` },
        },
      }}
    >
      {/* ── Header (fixed) ─────────────────────────────────────────── */}
      <Box
        sx={{
          px: 2.5,
          pt: 2.5,
          pb: 1.5,
          borderBottom: '1px solid',
          borderColor: 'divider',
          flexShrink: 0,
        }}
      >
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
          <Box>
            <Typography variant="overline" color="text.secondary" sx={{ lineHeight: 1.2 }}>
              Recommended Purchase Order
            </Typography>
            <Typography variant="h6" sx={{ mt: 0.25 }}>
              {vendorLabel}
            </Typography>
          </Box>
          <IconButton onClick={onClose} size="small" aria-label="Close" sx={{ mt: -0.5 }}>
            <CloseIcon />
          </IconButton>
        </Stack>

        {vendor ? (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
            {[vendor.contact_name, vendor.contact_email, anschriftEinzeiler(vendor.firmensitz)]
              .filter(Boolean)
              .join(' · ')}
          </Typography>
        ) : (
          <Skeleton variant="text" width={240} sx={{ mt: 1 }} />
        )}
      </Box>

      {/* ── Scrollable items ───────────────────────────────────────── */}
      <Box sx={{ flex: 1, overflowY: 'auto', px: 2.5, py: 2 }}>
        <Typography variant="overline" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
          {items.length} item{items.length !== 1 ? 's' : ''}
        </Typography>

        <AnimatePresence initial={false}>
          <Stack spacing={1}>
            {items.map((item, i) => {
              const p = produktJeZeile[i]
              const lage = moqLagen[i]
              const unit = Number(p?.cost_per_item) || 0
              const lineTotal = unit * (item.recommended_order_qty || 0)
              const variantName = item.variant_name || p?.variant_name || ''

              return (
                <motion.div
                  key={item.sku}
                  custom={i}
                  variants={itemVariants}
                  initial="hidden"
                  animate="visible"
                  exit="exit"
                  layout
                >
                  <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
                    <Stack direction="row" alignItems="flex-start" spacing={1.5}>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="body2" fontWeight={600} sx={{ lineHeight: 1.3 }}>
                          {item.product_name}
                        </Typography>
                        {variantName && (
                          <Typography variant="caption" color="text.secondary">
                            {variantName}
                          </Typography>
                        )}
                        <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.5 }} flexWrap="wrap">
                          <Typography variant="caption" color="text.disabled">
                            {item.sku}
                          </Typography>
                          {item.category_abcd && (
                            <Chip
                              label={`Cat. ${item.category_abcd}`}
                              size="small"
                              color={
                                item.category_abcd === 'A' ? 'success'
                                  : item.category_abcd === 'B' ? 'primary'
                                  : 'default'
                              }
                              sx={{ height: 18, fontSize: 10 }}
                            />
                          )}
                          {/* Ein gesetztes Datum kann hier NUR in der Zukunft
                              liegen — alles `<= heute` hat die Route schon
                              herausgefiltert. Also immer eine Ankuendigung,
                              nie ein bereits wirkender Auslauf.

                              🔴 Und genau deshalb `default` und das Wort
                              „announced", nicht `warning` und „Discontinued":
                              `ProductDetail.jsx:355-381` traegt die Korrektur
                              eines frueheren Gate 16 im eigenen Kommentar —
                              der Unterschied gehoert ins ERSTE WORT, und
                              `warning` ist der wirkenden Fassung vorbehalten.
                              Nachgemessen am ausgelieferten Wert: es gibt
                              keine `warning`-Palettenuebersteuerung, also
                              MUI-Standard #ed6c02 auf #ffffff = 3,11:1 bei
                              10 px — unter AA. Diese Fassung hatte beides
                              falsch und ist vom Regel-16-Gate gefallen. */}
                          {item.discontinued_at && (
                            <Tooltip
                              title={item.discontinued_note || 'No note recorded.'}
                              describeChild
                            >
                              {/* `tabIndex` macht den Chip erst zum Tabstopp —
                                  ohne ihn oeffnet der Tooltip nie per Fokus
                                  und die Notiz ist reine Mausware
                                  (`pattern-library.md:56-59`). */}
                              <Chip
                                label={`Discontinuation announced · ${formatAuslaufTag(item.discontinued_at)}`}
                                size="small"
                                tabIndex={0}
                                sx={{ height: 18, fontSize: 10 }}
                              />
                            </Tooltip>
                          )}
                        </Stack>
                      </Box>

                      <Stack alignItems="flex-end" spacing={0} sx={{ flexShrink: 0 }}>
                        <Typography variant="body2" fontWeight={600}>
                          {formatCurrency(lineTotal)}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {item.recommended_order_qty} × {formatCurrency(unit)}
                        </Typography>
                        {/* O21 — dieselbe Entscheidung wie in PODetail, aus
                            demselben Modul. Hier ist sie am meisten wert: der
                            Vorschlag rechnet MOQ-frei
                            (sync/sync_stockout_report.py:587), und DIESE Stelle
                            entscheidet, welche Zeile eine Bestellung wird. */}
                        {lage.art === MOQ_UNTERSCHRITTEN ? (
                          <Typography
                            variant="caption"
                            sx={(th) => ({ color: v2Tone(th, 'warn').main })}
                          >
                            MOQ {lage.moq} — {lage.fehlmenge} fehlen
                          </Typography>
                        ) : lage.art === MOQ_UNBEKANNT ? (
                          <Typography variant="caption" color="text.secondary">
                            MOQ unbekannt
                          </Typography>
                        ) : lage.art === MOQ_NICHT_LADBAR ? (
                          <Typography variant="caption" color="text.secondary">
                            MOQ nicht ladbar
                          </Typography>
                        ) : lage.art === MOQ_MENGE_UNKLAR ? (
                          <Typography variant="caption" color="text.secondary">
                            MOQ {lage.moq} — Menge unklar
                          </Typography>
                        ) : lage.art === MOQ_LAEDT ? (
                          <Typography variant="caption" color="text.secondary" fontStyle="italic">
                            MOQ wird geladen
                          </Typography>
                        ) : lage.moq ? (
                          <Typography variant="caption" color="text.secondary">
                            MOQ {lage.moq}
                          </Typography>
                        ) : (
                          <Typography variant="caption" color="text.secondary" fontStyle="italic">
                            keine MOQ hinterlegt
                          </Typography>
                        )}
                      </Stack>

                      <Stack direction="row" alignItems="center" sx={{ flexShrink: 0 }}>
                        <Tooltip title="More actions">
                          <IconButton
                            size="small"
                            aria-label={`More actions for ${item.product_name || item.sku}`}
                            onClick={(e) => openZeilenMenu(e, item)}
                            sx={{ color: 'text.secondary' }}
                          >
                            <MoreVertIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Decline — hides this suggestion for 14 days">
                          <Button
                            size="small"
                            color="inherit"
                            aria-label={`Decline ${item.product_name || item.sku}`}
                            onClick={() => handleDeclineItem(item.sku)}
                            sx={{ minWidth: 0, px: 1, fontSize: 11, color: 'text.secondary' }}
                          >
                            ✕
                          </Button>
                        </Tooltip>
                      </Stack>
                    </Stack>
                  </Paper>
                </motion.div>
              )
            })}
          </Stack>
        </AnimatePresence>

        {items.length === 0 && (
          <Typography variant="body2" color="text.secondary" align="center" sx={{ py: 4 }}>
            No items left.
          </Typography>
        )}
      </Box>

      {/* ── Cost summary (fixed, above actions) ────────────────────── */}
      {items.length > 0 && (
        <Box
          sx={{
            px: 2.5,
            pt: 1.5,
            pb: 0,
            borderTop: '1px solid',
            borderColor: 'divider',
            bgcolor: 'background.default',
            flexShrink: 0,
          }}
        >
          <Typography variant="overline" color="text.secondary" sx={{ display: 'block', mb: 0.75 }}>
            Cost Summary
          </Typography>
          <Stack spacing={0.4}>
            {/* 🔴 EK-B6 / UX-Gate A1 — hier standen ZWEI Zeilen:
                „Items subtotal" und „Grand total", getrennt von einem Divider.
                Seit die geschaetzte Fracht entfallen ist, trugen beide IMMER
                denselben Betrag. Eine Summe, die nichts summiert, ist schlechter
                als keine — also EINE Zeile, ehrlich beschriftet.

                🟢 NACHTRAG 2026-09-16, Owner-Entscheid O43 (a): der Hinweis
                darunter meldete eine DIVERGENZ — die angelegte Bestellung
                schaetzte weiter, die Vorschau nicht. Die Schaetzung ist
                entfallen, also gibt es die Divergenz nicht mehr. Der Hinweis
                bleibt trotzdem stehen, aber mit der umgekehrten Aussage: er
                sagt jetzt, dass auch die Bestellung keine Fracht traegt. Eine
                Flaeche, die eine fehlende Zahl gar nicht erwaehnt, laesst den
                Leser raten, ob sie vergessen wurde.

                Kommt die Fracht hier je zurueck, kommt die Doppelzeile mit. */}
            <Stack direction="row" justifyContent="space-between">
              <Typography variant="subtitle2">Total (excl. freight)</Typography>
              <Typography variant="subtitle2" fontWeight={700}>{formatCurrency(grandTotal)}</Typography>
            </Stack>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', pb: 1.5 }}>
              Freight is not estimated here — and the purchase order you create from
              this preview adds none of its own. Enter it once the carrier invoice is
              known.
            </Typography>
          </Stack>
        </Box>
      )}

      {/* ── Action buttons (pill-shaped, full-width) ───────────────── */}
      <Box
        sx={{
          px: 2.5,
          pt: 1,
          pb: isMobile ? 3 : 2,
          bgcolor: 'background.default',
          flexShrink: 0,
        }}
      >
        <Stack spacing={1}>
          <Button
            variant="contained"
            onClick={() => onAccept(group.vendor_id, items)}
            disabled={items.length === 0}
            fullWidth
            sx={{ py: 1.25, fontWeight: 600 }}
          >
            Accept as draft
          </Button>
          <Button
            variant="outlined"
            color="error"
            onClick={handleDeclineAll}
            disabled={items.length === 0}
            fullWidth
            sx={{ py: 1.25 }}
          >
            Decline all
          </Button>
        </Stack>
      </Box>

      {/* ── Zeilenmenü ─────────────────────────────────────────────── */}
      <Menu
        anchorEl={zeilenMenu.anchorEl}
        open={Boolean(zeilenMenu.anchorEl)}
        onClose={closeZeilenMenu}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        // 🔴 Sonst ein Wettlauf: das Menue gibt den Fokus waehrend seiner
        // Ausblendung an den ⋮-Knopf zurueck, waehrend die Fokusfalle des
        // Dialogs ihn nach innen zieht — sichtbar als Flackern auf einem
        // Knopf HINTER der Abdeckung.
        disableRestoreFocus
      >
        <MenuItem
          onClick={() => {
            setAuslaufFuer(zeilenMenu.item)
            closeZeilenMenu()
          }}
        >
          {zeilenMenu.item?.discontinued_at
            ? 'Edit discontinuation…'
            : 'Mark as discontinued…'}
        </MenuItem>
        {/* 🔴 Decline steht hier ZUSAETZLICH zum ✕ in der Zeile, nicht
            stattdessen. Grund ist nicht Bequemlichkeit: die beiden Aktionen
            unterscheiden sich in der Dauer (14 Tage gegen dauerhaft), und
            diese Unterscheidung stand bisher NUR im Tooltip — also nur fuer
            die Maus. Im Menue stehen beide Folgen nebeneinander lesbar, auch
            fuer Tastatur und Vorlesesoftware. Befund des Regel-16-Gates. */}
        <MenuItem
          onClick={() => {
            const sku = zeilenMenu.item?.sku
            closeZeilenMenu()
            if (sku) handleDeclineItem(sku)
          }}
        >
          Decline — hide for 14 days
        </MenuItem>
      </Menu>

      {/* Der Dialog SETZT und AENDERT, er nimmt nicht zurueck — das bleibt auf
          der Produktseite hinter dem Halten-Knopf. Begruendung im Kopf von
          `DiscontinuedDialog.jsx`. */}
      <DiscontinuedDialog
        open={Boolean(auslaufFuer)}
        sku={auslaufFuer?.sku}
        productName={auslaufFuer?.product_name}
        initialDate={auslaufFuer?.discontinued_at || ''}
        initialNote={auslaufFuer?.discontinued_note || ''}
        onClose={schliesseAuslauf}
        onSaved={(antwort) => handleDiscontinued(auslaufFuer.sku, antwort)}
      />
    </SwipeableDrawer>
  )
}

RecommendationPreviewDrawer.propTypes = {
  open: PropTypes.bool.isRequired,
  onOpen: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  group: PropTypes.shape({
    vendor_id: PropTypes.string,
    vendor_name: PropTypes.string,
    recommended_orders: PropTypes.arrayOf(PropTypes.object),
  }),
  products: PropTypes.array.isRequired,
  // O21 — 'laedt' | 'da' | 'fehler'. Ohne ihn waere ein gescheiterter
  // Stammdaten-Abruf von einer leeren Liste nicht zu unterscheiden.
  produktLage: PropTypes.oneOf(['laedt', 'da', 'fehler']),
  onAccept: PropTypes.func.isRequired,
  onDecline: PropTypes.func.isRequired,
  // Optional: ohne sie funktioniert die Aktion weiter, die Elternliste zieht
  // dann nur nicht nach. Nicht `isRequired`, damit ein zweiter Aufrufer den
  // Drawer ohne diese Verdrahtung einsetzen kann.
  onDiscontinued: PropTypes.func,
}
